import type { FredActionItem, FredAttachmentRef, FredConversationItem, FredEvent, FredMessageItem } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { ApiError, del, errorMessage, get, post } from "../../api";
import { uploadOne } from "../../lib/upload";
import { useSession } from "../../session";
import { Button, cx } from "../ui";
import { useSpeech } from "./useSpeech";

type Att = FredAttachmentRef & { localUrl: string | null; uploading: boolean; error?: string };
type Msg = Omit<FredMessageItem, "attachments" | "createdAt"> & { attachments: (FredAttachmentRef & { localUrl?: string | null })[]; status?: string; error?: string; streaming?: boolean; serverId?: string };
type Where = { lat: number; lon: number; accuracyM: number | null };

const AUTO_SEND_KEY = "fred.sendAfterSpeaking";
const LOCATION_KEY = "fred.shareLocation";
const readFlag = (key: string, fallback: boolean) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
};
const saveFlag = (key: string, on: boolean) => {
  try {
    localStorage.setItem(key, on ? "1" : "0");
  } catch {
    // Remembered for this visit only.
  }
};
/** Words that mean "where I am": the location is asked for even when sharing is off. */
const MEANS_HERE = /\b(here|my location|current location|where i am|i'?m at|this address|near me|nearby)\b/i;

/** The phone's position (asks permission the first time). Null when it's blocked or can't be found. */
function locate(): Promise<{ at: Where | null; blocked: boolean }> {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve({ at: null, blocked: false });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ at: { lat: p.coords.latitude, lon: p.coords.longitude, accuracyM: Number.isFinite(p.coords.accuracy) ? Math.round(p.coords.accuracy) : null }, blocked: false }),
      (err) => resolve({ at: null, blocked: err.code === err.PERMISSION_DENIED }),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  });
}
const LOCATION_BLOCKED = "Location is blocked for this site. On an iPhone: Settings → Privacy & Security → Location Services → Safari Websites (While Using), then allow it when asked. Elsewhere: the browser's site settings.";

/** POSTs and reads Fred's answer as it comes, one JSON event per line. */
export async function streamPost<E = FredEvent>(path: string, body: unknown, onEvent: (e: E) => void) {
  const res = await fetch(path, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", "x-nmt-csrf": "1" }, body: JSON.stringify(body) });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? "Something went wrong.");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) onEvent(JSON.parse(line) as E);
    }
  }
}

/** Fred's replies: plain text, with **bold** and "- " lists. */
export function FredText({ text }: { text: string }) {
  const inline = (s: string) => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : part));
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) blocks.push(<ul key={`l${blocks.length}`} className="ml-4 list-disc">{list.map((l, i) => <li key={i}>{inline(l)}</li>)}</ul>);
    list = [];
  };
  for (const line of text.split("\n")) {
    const m = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (m) list.push(m[1]!);
    else {
      flush();
      if (line.trim()) blocks.push(<p key={`p${blocks.length}`}>{inline(line)}</p>);
    }
  }
  flush();
  return <div className="flex flex-col gap-2 break-words">{blocks}</div>;
}

const STEP_LABEL: Record<string, string> = { done: "Done", failed: "Failed", skipped: "Not done", cancelled: "Not done" };

/** A text a change sends: shown in full, and editable before it's confirmed (Fred learns from the rewrite). */
function Draft({ text, editable, onSave }: { text: string; editable: boolean; onSave: (text: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!editing)
    return (
      <div className="mt-1 rounded-[8px] bg-mist px-2.5 py-1.5 text-[13px] whitespace-pre-wrap text-ink">
        {text}
        {editable && (
          <button className="ml-2 text-xs font-semibold text-brand hover:underline" onClick={() => (setValue(text), setEditing(true))}>
            Edit
          </button>
        )}
      </div>
    );
  return (
    <div className="mt-1 flex flex-col gap-1.5">
      <textarea className="w-full rounded-[8px] border border-line px-2.5 py-1.5 text-base sm:text-[13px]" rows={Math.min(12, Math.ceil(value.length / 40) + 2)} value={value} onChange={(e) => setValue(e.target.value)} />
      {error && <div className="text-xs text-danger">{error}</div>}
      <div className="flex gap-2">
        <Button
          variant="primary"
          className="px-3 py-1"
          busy={saving}
          disabled={!value.trim()}
          onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              await onSave(value.trim());
              setEditing(false);
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setSaving(false);
            }
          }}
        >
          Save
        </Button>
        <Button className="px-3 py-1" disabled={saving} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ActionCard({ action, onConfirm, onCancel, onDraft, busy }: { action: FredActionItem; onConfirm: () => void; onCancel: () => void; onDraft: (index: number, text: string) => Promise<void>; busy: boolean }) {
  const proposed = action.status === "proposed";
  return (
    <div className={cx("rounded-[10px] border bg-white p-3", proposed ? "border-brand/40" : "border-line")}>
      <div className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">{proposed ? (action.steps.length > 1 ? `${action.steps.length} changes to confirm` : "Confirm this change") : action.status === "expired" ? "Expired" : action.status === "cancelled" ? "Cancelled" : "Changes"}</div>
      <ol className="flex flex-col gap-2">
        {action.steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className={cx("mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-bold", s.status === "done" ? "bg-ok-wash text-ok" : s.status === "failed" ? "bg-danger-wash text-danger" : s.status === "proposed" ? "bg-brand-wash text-brand" : "bg-mist text-muted")}>
              {s.status === "done" ? "✓" : s.status === "failed" ? "!" : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className={cx(s.status === "cancelled" || s.status === "skipped" ? "text-muted line-through" : "text-ink")}>{s.summary}</div>
              {s.draft !== undefined && <Draft text={s.draft} editable={proposed && !busy} onSave={(text) => onDraft(i, text)} />}
              {s.status !== "proposed" && <div className={cx("text-xs", s.status === "failed" ? "text-danger" : "text-muted")}>{s.result ?? STEP_LABEL[s.status]}</div>}
              {s.link && (
                <Link to={s.link.path} className="text-xs font-semibold text-brand hover:underline">
                  {s.link.label} →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
      {proposed && (
        <>
          <div className="mt-3 flex gap-2">
            <Button variant="primary" className="flex-1 py-1.5" busy={busy} onClick={onConfirm}>
              {action.steps.length > 1 ? "Do all" : "Confirm"}
            </Button>
            <Button className="py-1.5" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
          </div>
          <div className="mt-2 text-xs text-muted">Or tell Fred what to change.</div>
        </>
      )}
    </div>
  );
}

/** Thumbs up or down on a reply; a thumbs-down asks what Fred should have done (admins see it in Settings → AI). */
function Rating({ rating, onRate }: { rating: "up" | "down" | null; onRate: (rating: "up" | "down", comment?: string) => void }) {
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const thumb = (r: "up" | "down") => (
    <button
      aria-label={r === "up" ? "Good answer" : "Not right"}
      aria-pressed={rating === r}
      title={r === "up" ? "Good answer" : "Not right"}
      className={cx("rounded p-1", rating === r ? "text-brand" : "text-muted/70 hover:text-ink")}
      onClick={() => (onRate(r), setAsking(r === "down"), setSent(false))}
    >
      <svg viewBox="0 0 24 24" className={cx("size-4", r === "down" && "rotate-180")} fill={rating === r ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
        <path d="M7 10v10H4V10zM7 10l4-7a2 2 0 0 1 2 2v4h5.5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.3 20H7" />
      </svg>
    </button>
  );
  return (
    <div className="-mt-1 flex flex-col gap-1.5">
      <div className="flex gap-0.5">
        {thumb("up")}
        {thumb("down")}
        {sent && <span className="self-center pl-1 text-xs text-muted">Thanks. An admin will see it.</span>}
      </div>
      {asking && (
        <div className="flex gap-1.5">
          <input
            className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:text-sm"
            placeholder="What should Fred have done?"
            value={comment}
            maxLength={1000}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && comment.trim() && (onRate("down", comment.trim()), setAsking(false), setSent(true))}
          />
          <Button className="px-3 py-1" disabled={!comment.trim()} onClick={() => (onRate("down", comment.trim()), setAsking(false), setSent(true))}>
            Send
          </Button>
        </div>
      )}
    </div>
  );
}

const fredIcon = (
  <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8zM19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9zM5 15l.6 1.4L7 17l-1.4.6L5 19l-.6-1.4L3 17l1.4-.6z" />
  </svg>
);

/** The button that opens Fred (top bar). */
export function FredButton({ onClick, className, dark }: { onClick: () => void; className?: string; dark?: boolean }) {
  return (
    <button
      onClick={onClick}
      title="Ask Fred (Ctrl+K)"
      aria-label="Ask Fred"
      className={cx(dark ? "rounded-md p-2 text-white hover:bg-white/10" : "flex items-center gap-1.5 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-sm font-semibold text-ink-soft hover:border-brand hover:text-brand", className)}
    >
      {fredIcon}
      {!dark && <span>Fred</span>}
    </button>
  );
}

/**
 * Fred, the assistant: a chat that drops in from the side (full screen on a phone). Type, talk, or
 * paste a screenshot; Fred looks things up and proposes changes, which happen when you confirm.
 */
export function FredPanel({ open, openId = null, onClose }: { open: boolean; openId?: string | null; onClose: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const location = useLocation();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [atts, setAtts] = useState<Att[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [autoSend, setAutoSend] = useState(() => readFlag(AUTO_SEND_KEY, true));
  const [shareLocation, setShareLocation] = useState(() => readFlag(LOCATION_KEY, false));
  const [locationNote, setLocationNote] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const typedBefore = useRef("");

  const status = useQuery({ queryKey: ["fred", "status"], queryFn: () => get<{ available: boolean }>("/api/fred/status"), enabled: open && can("fred.use"), staleTime: 5 * 60_000 });
  const history = useQuery({ queryKey: ["fred", "conversations"], queryFn: () => get<FredConversationItem[]>("/api/fred/conversations"), enabled: open && showHistory });

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  /** Applies a streamed event to the reply being written (the last message). */
  const apply = useCallback(
    (e: FredEvent) => {
      if (e.type === "conversation") return setConversationId(e.id);
      if (e.type === "changed") return void qc.invalidateQueries();
      setMessages((all) => {
        const out = [...all];
        if (e.type === "action") {
          // An update to a card already shown (confirmed, cancelled).
          const at = out.findIndex((m) => m.action?.id === e.action.id);
          if (at >= 0) {
            out[at] = { ...out[at]!, action: e.action };
            return out;
          }
        }
        const last = { ...out[out.length - 1]! };
        out[out.length - 1] = last;
        if (e.type === "text") (last.text += e.text), (last.status = undefined);
        else if (e.type === "message") last.serverId = e.id;
        else if (e.type === "status") last.status = e.text;
        else if (e.type === "link") last.links = [...last.links, { path: e.path, label: e.label }];
        else if (e.type === "action") (last.action = e.action), (last.status = undefined);
        else if (e.type === "error") (last.error = e.message), (last.status = undefined);
        else if (e.type === "done") (last.streaming = false), (last.status = undefined);
        return out;
      });
    },
    [qc],
  );

  const reply = (): Msg => ({ id: crypto.randomUUID(), role: "assistant", text: "", attachments: [], links: [], action: null, rating: null, streaming: true, status: "Thinking…" });

  // False when the connection failed (the reply never came).
  async function run(path: string, body: unknown): Promise<boolean> {
    setBusy(true);
    // No answer at all (only an error): it didn't get through.
    let answered = false;
    let failed = false;
    try {
      await streamPost<FredEvent>(path, body, (e) => {
        if (e.type === "error") failed = true;
        else if (e.type === "text" || e.type === "action" || e.type === "link") answered = true;
        apply(e);
      });
      return answered || !failed;
    } catch (err) {
      apply({ type: "error", message: errorMessage(err) });
      return false;
    } finally {
      apply({ type: "done" });
      setBusy(false);
    }
  }

  async function send(override?: string) {
    const said = (override ?? text).trim();
    const ready = atts.filter((a) => !a.uploading && !a.error);
    if (busy || (!said && !ready.length) || atts.some((a) => a.uploading)) return;
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", text: said, attachments: ready, links: [], action: null, rating: null }, reply()]);
    setText("");
    setAtts([]);
    let at: Where | null = null;
    if (shareLocation || MEANS_HERE.test(said)) {
      apply({ type: "status", text: "Getting your location…" });
      const found = await locate();
      at = found.at;
      setLocationNote(found.blocked ? LOCATION_BLOCKED : null);
      apply({ type: "status", text: "Thinking…" });
    }
    const ok = await run("/api/fred/chat", { conversationId, text: said, attachmentIds: ready.map((a) => a.id), page: location.pathname, location: at });
    // Not answered: put what they said back in the box (and the files back) to send again.
    if (!ok) {
      setText((t) => t || said);
      setAtts((a) => (a.length ? a : ready));
    }
  }

  async function confirm(action: FredActionItem) {
    setConfirming(action.id);
    setMessages((m) => [...m, reply()]);
    await run(`/api/fred/actions/${action.id}/confirm`, { page: location.pathname });
    setConfirming(null);
    // Drop an empty reply (nothing more to say).
    setMessages((m) => (m.length && !m[m.length - 1]!.text && !m[m.length - 1]!.error && !m[m.length - 1]!.links.length && !m[m.length - 1]!.action ? m.slice(0, -1) : m));
  }

  async function toggleLocation() {
    if (shareLocation) {
      setShareLocation(false);
      saveFlag(LOCATION_KEY, false);
      setLocationNote(null);
      return;
    }
    // Asks the browser's permission now, so the first message doesn't wait on it.
    const found = await locate();
    if (!found.at) return setLocationNote(found.blocked ? LOCATION_BLOCKED : "Couldn't find your location. Try again outside or with Wi-Fi on.");
    setLocationNote(null);
    setShareLocation(true);
    saveFlag(LOCATION_KEY, true);
  }

  async function rate(m: Msg, rating: "up" | "down", comment = "") {
    if (!m.serverId) return;
    setMessages((all) => all.map((x) => (x.id === m.id ? { ...x, rating } : x)));
    await post(`/api/fred/messages/${m.serverId}/feedback`, { rating, comment }).catch(() => undefined);
  }

  async function editDraft(action: FredActionItem, index: number, text: string) {
    apply({ type: "action", action: await post<FredActionItem>(`/api/fred/actions/${action.id}/draft`, { index, text }) });
  }

  async function cancel(action: FredActionItem) {
    try {
      apply({ type: "action", action: await post<FredActionItem>(`/api/fred/actions/${action.id}/cancel`) });
    } catch (err) {
      setMessages((m) => [...m, { ...reply(), streaming: false, status: undefined, error: errorMessage(err) }]);
    }
  }

  function addFiles(files: FileList | File[]) {
    for (const file of [...files].slice(0, 10)) {
      const local: Att = { id: crypto.randomUUID(), name: file.name || "Screenshot.png", contentType: file.type, thumbUrl: null, localUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null, uploading: true };
      const named = file.name ? file : new File([file], local.name, { type: file.type });
      setAtts((a) => [...a, local]);
      uploadOne("/api/fred/attachments", named)
        .then((saved) => setAtts((a) => a.map((x) => (x.id === local.id ? { ...x, ...(saved as unknown as FredAttachmentRef), localUrl: x.localUrl, uploading: false } : x))))
        .catch((err) => setAtts((a) => a.map((x) => (x.id === local.id ? { ...x, uploading: false, error: errorMessage(err) } : x))));
    }
  }

  const speech = useSpeech(
    (heard) => setText(`${typedBefore.current}${typedBefore.current ? " " : ""}${heard}`),
    (heard) => {
      const full = `${typedBefore.current}${typedBefore.current ? " " : ""}${heard}`;
      if (autoSend) void send(full);
    },
  );

  // Asked to open a conversation (Fred's inbox → Open with Fred).
  useEffect(() => {
    if (open && openId && openId !== conversationId) void openConversation(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openId]);

  async function openConversation(id: string) {
    setShowHistory(false);
    try {
      const c = await get<{ id: string; messages: FredMessageItem[] }>(`/api/fred/conversations/${id}`);
      setConversationId(c.id);
      // A message Fred couldn't answer shows why under it, as it did when it happened.
      setMessages(c.messages.flatMap((m): Msg[] => [{ ...m, error: undefined, serverId: m.id }, ...(m.role === "user" && m.error ? [{ ...reply(), streaming: false, status: undefined, error: m.error }] : [])]));
    } catch (err) {
      setMessages([{ ...reply(), streaming: false, status: undefined, error: errorMessage(err) }]);
    }
  }

  function newChat() {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
    inputRef.current?.focus();
  }

  if (!open) return null;
  const off = status.data && !status.data.available;

  return (
    <div
      className="fixed inset-0 z-50 flex w-full max-w-[100vw] flex-col overflow-x-hidden overscroll-contain bg-paper shadow-2xl lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[420px] lg:border-l lg:border-line"
      role="dialog"
      aria-label="Fred, the assistant"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) (e.preventDefault(), setDragging(true));
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
      }}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <header className="sidebar flex items-center gap-2 px-3 py-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] text-white">
        <span className="grid size-8 place-items-center rounded-full bg-brand">{fredIcon}</span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight">Fred</div>
          <div className="truncate text-xs text-white/60">Asks before changing anything</div>
        </div>
        <button className="rounded-md px-2 py-1.5 text-sm text-white/80 hover:bg-white/10" onClick={() => setShowHistory((s) => !s)} aria-expanded={showHistory}>
          History
        </button>
        <button className="rounded-md px-2 py-1.5 text-sm text-white/80 hover:bg-white/10" onClick={newChat}>
          New
        </button>
        <button className="rounded-md p-2 hover:bg-white/10" onClick={onClose} aria-label="Close Fred">
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      {showHistory && (
        <div className="max-h-[50%] overflow-y-auto border-b border-line bg-white">
          {history.isLoading && <div className="p-3 text-sm text-muted">Loading…</div>}
          {history.data?.length === 0 && <div className="p-3 text-sm text-muted">No chats yet.</div>}
          {history.data?.map((c) => (
            <div key={c.id} className="flex items-center border-b border-mist last:border-0">
              <button className="min-w-0 flex-1 truncate px-3 py-2.5 text-left text-sm hover:bg-mist" onClick={() => void openConversation(c.id)}>
                {c.title || "Chat"}
                <span className="ml-2 text-xs text-muted">{new Date(c.updatedAt).toLocaleDateString()}</span>
              </button>
              <button
                className="px-3 py-2.5 text-xs text-muted hover:text-danger"
                aria-label="Delete this chat"
                onClick={async () => {
                  await del(`/api/fred/conversations/${c.id}`);
                  if (c.id === conversationId) newChat();
                  void history.refetch();
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-4">
        {off && <div className="rounded-[10px] bg-wait-wash p-3 text-sm text-wait">Fred isn't set up on this server yet.</div>}
        {!messages.length && !off && (
          <div className="m-auto max-w-xs text-center text-sm text-muted">
            <div className="mb-2 text-base font-semibold text-ink">Hi, I'm Fred.</div>
            Ask me to find a job, add a contact or a job, take a note, set up a task or appointment, or save photos. Paste a screenshot or attach files, or tap the mic and talk.
            <div className="mt-3 flex flex-col gap-1.5">
              {["What's on my plate today?", "New job for…", "Add a note to this job"].map((s) => (
                <button key={s} className="rounded-full border border-line bg-white px-3 py-1.5 text-ink-soft hover:border-brand hover:text-brand" onClick={() => (s.endsWith("…") ? (setText(s.slice(0, -1)), inputRef.current?.focus()) : void send(s))}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="ml-10 flex min-w-0 flex-col items-end gap-1.5">
              {!!m.attachments.length && (
                <div className="flex flex-wrap justify-end gap-1.5">
                  {m.attachments.map((a) => (a.localUrl || a.thumbUrl ? <img key={a.id} src={a.localUrl ?? a.thumbUrl ?? ""} alt={a.name} className="size-16 rounded-md object-cover" /> : <span key={a.id} className="rounded-md bg-mist px-2 py-1 text-xs">{a.name}</span>))}
                </div>
              )}
              {m.text && <div className="max-w-full rounded-[14px] rounded-br-sm bg-brand px-3 py-2 text-[15px] whitespace-pre-wrap text-white [overflow-wrap:anywhere]">{m.text}</div>}
            </div>
          ) : (
            <div key={m.id} className="mr-6 flex min-w-0 flex-col gap-2 text-[15px] text-ink">
              {m.text && (
                <div className="rounded-[14px] rounded-bl-sm bg-white px-3 py-2 shadow-sm [overflow-wrap:anywhere]">
                  <FredText text={m.text} />
                  {m.streaming && !m.status && <span className="mt-1 inline-block size-2 animate-pulse rounded-full bg-muted" aria-label="Fred is writing" />}
                </div>
              )}
              {m.status && (
                <div className="flex items-center gap-2 text-sm text-muted">
                  <span className="size-3 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden />
                  {m.status}
                </div>
              )}
              {m.action && <ActionCard action={m.action} busy={confirming === m.action.id} onConfirm={() => void confirm(m.action!)} onCancel={() => void cancel(m.action!)} onDraft={(index, text) => editDraft(m.action!, index, text)} />}
              {!!m.links.length && (
                <div className="flex flex-wrap gap-2">
                  {m.links.map((l, i) => (
                    <Link key={i} to={l.path} onClick={() => window.innerWidth < 1024 && onClose()} className="rounded-full border border-brand/40 bg-white px-3 py-1 text-sm font-semibold text-brand hover:bg-brand-wash">
                      {l.label} →
                    </Link>
                  ))}
                </div>
              )}
              {m.error && <div className="rounded-[10px] bg-danger-wash px-3 py-2 text-sm text-danger">{m.error}</div>}
              {m.serverId && m.text && !m.streaming && <Rating rating={m.rating} onRate={(r, c) => void rate(m, r, c)} />}
            </div>
          ),
        )}
      </div>

      {dragging && <div className="pointer-events-none absolute inset-0 grid place-items-center bg-brand/10 text-lg font-semibold text-brand">Drop to attach</div>}

      <div className="border-t border-line bg-white px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {!!atts.length && (
          <div className="mb-2 flex flex-wrap gap-2">
            {atts.map((a) => (
              <div key={a.id} className={cx("relative flex items-center gap-1.5 rounded-md border bg-paper py-1 pr-6 pl-1 text-xs", a.error ? "border-danger text-danger" : "border-line")} title={a.error}>
                {a.localUrl ? <img src={a.localUrl} alt="" className="size-8 rounded object-cover" /> : <span className="grid size-8 place-items-center rounded bg-mist text-[10px] uppercase">{a.name.split(".").pop()?.slice(0, 4)}</span>}
                <span className="max-w-[120px] truncate">{a.error ? "Didn't upload" : a.name}</span>
                {a.uploading && <span className="size-3 animate-spin rounded-full border-2 border-current border-r-transparent" aria-label="Uploading" />}
                <button className="absolute top-0.5 right-1 text-muted hover:text-ink" aria-label={`Remove ${a.name}`} onClick={() => setAtts((x) => x.filter((y) => y.id !== a.id))}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {speech.error && <div className="mb-1 text-xs text-danger">{speech.error}</div>}
        {locationNote && <div className="mb-1 text-xs text-danger">{locationNote}</div>}
        <div className="flex items-end gap-1.5">
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" onChange={(e) => (e.target.files && addFiles(e.target.files), (e.target.value = ""))} />
          <button className="rounded-md p-2 text-ink-soft hover:bg-mist" aria-label="Attach files or photos" title="Attach files or photos" onClick={() => fileRef.current?.click()}>
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />
            </svg>
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            placeholder={speech.listening ? "Listening…" : "Ask Fred…"}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              const files = [...e.clipboardData.files];
              if (files.length) (e.preventDefault(), addFiles(files));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) (e.preventDefault(), void send());
            }}
            className="max-h-36 min-h-[40px] min-w-0 flex-1 resize-none rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
            style={{ fieldSizing: "content" } as React.CSSProperties}
          />
          <button
            className={cx("rounded-md p-2", shareLocation ? "text-brand" : "text-ink-soft hover:bg-mist")}
            aria-label={shareLocation ? "Stop sharing my location with Fred" : "Share my location with Fred"}
            title={shareLocation ? "Sharing your location with Fred (tap to stop)" : "Share my location with Fred"}
            aria-pressed={shareLocation}
            onClick={() => void toggleLocation()}
          >
            <svg viewBox="0 0 24 24" className="size-5" fill={shareLocation ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
              <circle cx="12" cy="9.5" r="2.5" fill={shareLocation ? "white" : "none"} />
            </svg>
          </button>
          {speech.supported && (
            <button
              className={cx("rounded-md p-2", speech.listening ? "animate-pulse bg-brand text-white" : "text-ink-soft hover:bg-mist")}
              aria-label={speech.listening ? "Stop listening" : "Talk to Fred"}
              title={speech.listening ? "Stop listening" : "Talk to Fred"}
              aria-pressed={speech.listening}
              onClick={() => (speech.listening ? speech.stop() : ((typedBefore.current = text.trim()), speech.start()))}
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
            </button>
          )}
          <button className="rounded-md bg-brand p-2 text-white hover:bg-brand-deep disabled:bg-brand/40" aria-label="Send" disabled={busy || off || (!text.trim() && !atts.some((a) => !a.uploading && !a.error)) || atts.some((a) => a.uploading)} onClick={() => void send()}>
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
        {speech.supported && (
          <label className="mt-1.5 flex items-center gap-1.5 text-xs text-muted">
            <input
              type="checkbox"
              checked={autoSend}
              onChange={(e) => {
                setAutoSend(e.target.checked);
                try {
                  localStorage.setItem(AUTO_SEND_KEY, e.target.checked ? "1" : "0");
                } catch {
                  // Remembered for this visit only.
                }
              }}
            />
            Send when I stop talking
          </label>
        )}
      </div>
    </div>
  );
}
